/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {MinusIcon, PlusIcon} from "@heroicons/react/24/outline";

interface BookingPackageItemsWithKartingCountInputProps {
    setKarting: (kartingCount: number) => void;
    karting: number;
    label: string;
    maxCount: number;
    disable?: boolean;
    isKartingDisabled?: boolean;
}

const BookingPackageItemsWithKartingCountInput = (props: BookingPackageItemsWithKartingCountInputProps) => {
    const {t} = useTranslation();
    const [karting, setKarting] = useState<number>(props?.karting ? props.karting : 0);

    const setKartingCountHandler = (count: number) => {
        if (props?.disable || props.isKartingDisabled) {
            return;
        }

        const newCount: number = props.karting + count;
        if (newCount > props.maxCount || newCount < 0) {
            return;
        }
        setKarting(newCount);
        props.setKarting(newCount);
    };

    const setKartingInputHandler = (count: string) => {
        if (props?.disable || props.isKartingDisabled) {
            return;
        }

        if (+count > props.maxCount || +count < 0) {
            return;
        }
        props.setKarting(+count);
    };

    useEffect(() => {
        setKarting(props.karting);
    }, [props.karting]);

    return (
        <div
            className={clsx(
                "BookingPackageItemsWithKartingCountInput flex flex-col items-center justify-center min-w-[120px] max-w-[120px] sm:max-w-[144px] w-full",
                    props?.disable && "opacity-50 cursor-not-allowed",
                )}
        >
            <div className={"font-medium text-sm border border-b-0 rounded-tl rounded-tr px-2 py-1.5 w-full text-center grow"}>{props.label}</div>
            <div className={"flex flex-row items-center justify-center w-full"}>
                <button
                    disabled={props.disable}
                    className={clsx(
                        "border rounded-bl flex flex-row justify-center items-center px-2 py-2 font-bold text-lg leading-none min-w-[40px] sm:min-w-[44px] min-h-[40px]",
                        props?.disable && "opacity-50 cursor-not-allowed",
                    )}
                    type={"button"}
                    onClick={() => setKartingCountHandler(-1)}
                >
                    <MinusIcon className={"pointer-events-none w-5 h-5"} />
                </button>
                <input
                    disabled={props.disable}
                    className={clsx(
                        "border-t border-b flex h-full min-h-[40px]  min-w-[40px]  w-full px-1 text-center font-medium placeholder-black hover:appearance-none",
                        props?.disable && "opacity-50 cursor-not-allowed",
                    )}
                    inputMode={"numeric"}
                    max={props.maxCount}
                    min={0}
                    name={"withKarting"}
                    type={"text"}
                    value={karting}
                    onChange={e => setKartingInputHandler(e.target.value)}
                />
                <button
                    disabled={props.disable}
                    className={clsx(
                        "border  rounded-br flex flex-row justify-center items-center px-2 py-2 font-bold text-lg leading-none min-w-[40px] sm:min-w-[44px] min-h-[40px]",
                        props?.disable && "opacity-50 cursor-not-allowed",
                    )}
                    type={"button"}
                    onClick={() => setKartingCountHandler(+1)}
                >
                    <PlusIcon className={"pointer-events-none w-5 h-5"} />
                </button>
            </div>
        </div>
    );
};

export default BookingPackageItemsWithKartingCountInput;
